import { ref } from 'vue'
import { storeToRefs } from 'pinia'

import { useUserStore } from '@/stores/userStore'

import useBlProviders from '@/composables/providers/useBlProvider'

// BASE
import useLegalEntities from '@/composables/data/useLegalEntities'
import useClusters from '@/composables/data/useClusters'

// PERSONS
import useStaff from '@/composables/data/useStaff'
import useStaffGroups from '@/composables/data/useStaffGroups'
import useStaffRoles from '@/composables/data/useStaffRoles'
import useStaffDepartments from '@/composables/data/useStaffDepartments'
import useCounterparties from '@/composables/data/useCounterparties.js'
import useCounterpartiesGroups from '@/composables/data/useCounterpartiesGroups.js'

// FIELDS
import useFields from '@/composables/data/useFields.js'
import useFieldsTags from '@/composables/data/useFieldsTags.js'

// LANDS
import useLandActions from '@/composables/data/useLandActions.js'
import useLandCategories from '@/composables/data/useLandCategories.js'
import useLandOwnershipTypes from '@/composables/data/useLandOwnershipTypes.js'
import useLandPurposes from '@/composables/data/useLandPurposes.js'

import useWeightStations from '@/composables/data/useWeightStations.js'
import useInfrastructure from '@/composables/data/useInfrastructures'
import useMachines from '@/composables/data/useMachines'
import useMaterials from '@/composables/data/useMaterials'
import useEquipment from '@/composables/data/useEquipment'
import useWorks from '@/composables/data/useWorks'
import useWorksCategories from './useWorksCategories'
import useWorksShifts from '@/composables/data/useWorksShifts.js'
import useWorkReportMeasures from '@/composables/data/useWorkReportMeasures'
import useWorkReportLinks from '@/composables/data/useWorkReportLinks.js'
import useWorkReportPhotos from '@/composables/data/useWorkReportPhotos.js'
import useWorkReportDocuments from '@/composables/data/useWorkReportDocuments.js'
import useRegulationTags from '@/composables/data/useRegulationTags.js'
import useRegulationsStageTags from '@/composables/data/useRegulationsStageTags.js'
import useInfrastructureTags from '@/composables/data/useInfrastructureTags.js'
import useInfrastructureTypes from '@/composables/data/useInfrastructureTypes.js'
import useLandsCadastralCases from '@/composables/data/useLandsCadastralCases.js'
import useIncidentTags from '@/composables/data/useIncidentTags.js'
import useDevices from '@/composables/data/useDevices'
import useMachinesGroups from '@/composables/data/useMachinesGroups.js'
import useEquipmentGroups from '@/composables/data/useEquipmentGroups.js'
import useLogisticRouteTags from '@/composables/data/useLogisticRouteTags.js'
import useFinanceCategories from '@/composables/data/useFinanceCategories.js'
import useFinanceGroups from '@/composables/data/useFinanceGroups.js'
import useFinanceTags from '@/composables/data/useFinanceTags.js'

import useMaterialsCategories from './useMaterialsCategories'
import useMaterialsTags from './useMaterialsTags'
import useRegulations from '@/composables/data/useRegulations'
import useFuelStations from '@/composables/data/useFuelStations'
import useWorksProductivityNorms from '@/composables/data/useWorksProductivityNorms'
import useWorksQualityNorms from '@/composables/data/useWorksQualityNorms'
import useWorksServicePaymentRateNorms from '@/composables/data/useWorksServicePaymentRateNorms'
import useWorksStaffPaymentRatesNorms from '@/composables/data/useWorksStaffPaymentRatesNorms'
import useCrops from '@/composables/data/useCrops'
import useWorksMaterialConsumptionRateNorms from '@/composables/data/useWorksMaterialConsumptionRateNorms'
import useIdentityCards from '@/composables/data/useIdentityCards'
import useLands from '@/composables/data/useLands'
import useNotifications from '@/composables/data/useNotifications'

export default function useCashedData() {
	const { getCurrentVersions } = useBlProviders()
	const userStore = useUserStore()
	const { selectedCompanyId } = storeToRefs(userStore)

	const isLoadingCashedData = ref(false)

	const blParams = {
		showErrorNotificationForLoadingAllEntities: false,
		useLocalRefs: false,
	}

	// BASE
	const { getFulledLegalEntities } = useLegalEntities(blParams)
	const { getFullFieldClusters } = useClusters(blParams)

	// PERSONS
	const { getFulledStaff } = useStaff(blParams)
	const { getStaffGroups } = useStaffGroups(blParams)
	const { getStaffRoles } = useStaffRoles(blParams)
	const { getStaffDepartments } = useStaffDepartments(blParams)
	const { getFulledCounterparties } = useCounterparties(blParams)
	const { getCounterpartiesGroups } = useCounterpartiesGroups(blParams)

	// FIELDS
	const { getFullFields } = useFields(blParams)
	const { getFieldsTags } = useFieldsTags(blParams)

	// LANDS
	const { getLandActions } = useLandActions(blParams)
	const { getLandCategories } = useLandCategories(blParams)
	const { getLandOwnershipTypes } = useLandOwnershipTypes(blParams)
	const { getLandPurposes } = useLandPurposes(blParams)

	const { getInfrastructures } = useInfrastructure(blParams)

	const { getWeightStations } = useWeightStations(blParams)

	const { getFulledMachines } = useMachines(blParams)

	const { getFullEquipment } = useEquipment(blParams)

	const { getFulledMaterials } = useMaterials(blParams)
	const { getMaterialsCategories } = useMaterialsCategories(blParams)
	const { getMaterialsTags } = useMaterialsTags(blParams)

	const { getWorks } = useWorks(blParams)

	const { getWorksCategories } = useWorksCategories(blParams)

	const { getWorksShifts } = useWorksShifts(blParams)

	const { getWorkReportMeasures } = useWorkReportMeasures(blParams)

	const { getWorkReportLinks } = useWorkReportLinks(blParams)

	const { getWorkReportPhotos } = useWorkReportPhotos(blParams)

	const { getWorkReportDocuments } = useWorkReportDocuments(blParams)

	const { getRegulationTags } = useRegulationTags(blParams)

	const { getRegulationsStageTags } = useRegulationsStageTags(blParams)

	const { getInfrastructureTags } = useInfrastructureTags(blParams)

	const { getFulledInfrastructureTypes } = useInfrastructureTypes(blParams)

	const { getFullLandsCadastralCases } = useLandsCadastralCases(blParams)

	const { getIncidentTags } = useIncidentTags(blParams)

	const { getFulledDevices } = useDevices(blParams)

	const { getFinanceCategories } = useFinanceCategories(blParams)

	const { getFinanceGroups } = useFinanceGroups(blParams)

	const { getFinanceTags } = useFinanceTags(blParams)

	const { getMachinesGroups } = useMachinesGroups(blParams)

	const { getEquipmentGroups } = useEquipmentGroups(blParams)

	const { getLogisticRouteTags } = useLogisticRouteTags(blParams)

	const { getRegulations } = useRegulations(blParams)
	const { getFuelStations } = useFuelStations(blParams)
	const { getWorksProductivityNorms } = useWorksProductivityNorms(blParams)
	const { getWorksQualityNorms } = useWorksQualityNorms(blParams)
	const { getFulledWorksServicePaymentRateNorm } = useWorksServicePaymentRateNorms(blParams)
	const { getWorksStaffPaymentRatesNorms } = useWorksStaffPaymentRatesNorms(blParams)
	const { getCrops } = useCrops(blParams)
	const { getFulledWorksMaterialConsumptionRateNorm } =
		useWorksMaterialConsumptionRateNorms(blParams)
	const { getFulledIdentityCards } = useIdentityCards(blParams)
	const { getFullLands } = useLands(blParams)
	const { getNotificationsUnreadCount } = useNotifications()

	const updateCacheData = async () => {
		isLoadingCashedData.value = true
		/* const versions =  */ await getCurrentVersions(selectedCompanyId.value)

		const cacheBlMethods = [
			getFulledLegalEntities,
			getFullFieldClusters,

			getFulledStaff,
			getStaffGroups,
			getStaffRoles,
			getStaffDepartments,
			getFulledCounterparties,
			getCounterpartiesGroups,

			getFullFields,
			getFieldsTags,

			getLandActions,
			getLandCategories,
			getLandOwnershipTypes,
			getLandPurposes,

			getInfrastructures,
			getWeightStations,
			getFulledMachines,
			getFullEquipment,
			getFulledMaterials,
			getWorks,
			getWorkReportMeasures,
			getWorkReportLinks,
			getWorkReportPhotos,
			getWorkReportDocuments,
			getRegulationTags,
			getRegulationsStageTags,
			getIncidentTags,
			getFulledDevices,
			getWorksShifts,
			getWorksCategories,
			getMachinesGroups,
			getEquipmentGroups,
			getLogisticRouteTags,
			getFinanceCategories,
			getFinanceGroups,
			getFinanceTags,
			getMaterialsCategories,
			getMaterialsTags,
			getRegulations,
			getFuelStations,
			getInfrastructureTags,
			getFulledInfrastructureTypes,
			getFullLandsCadastralCases,
			getWorksProductivityNorms,
			getWorksQualityNorms,
			getFulledWorksServicePaymentRateNorm,
			getWorksStaffPaymentRatesNorms,
			getCrops,
			getFulledWorksMaterialConsumptionRateNorm,
			getFulledIdentityCards,
			getFullLands,
			getNotificationsUnreadCount,
		]

		const blRequests = cacheBlMethods.map((requestMethod) => {
			return requestMethod()
		})

		try {
			await Promise.all(blRequests)
			isLoadingCashedData.value = false
			return true
		} catch {
			isLoadingCashedData.value = false
			return false
		}
	}

	return {
		isLoadingCashedData,
		updateCacheData,
	}
}
